import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AdminUser } from '@reflact/ipkv-types/dist/Admin';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { FloatLabel } from 'primeng/floatlabel';
import { InplaceModule } from 'primeng/inplace';
import { InputTextModule } from 'primeng/inputtext';
import { MenubarModule } from 'primeng/menubar';
import { Message } from 'primeng/message';
import { TableEditCompleteEvent, TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { User } from '../../services/user';

@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    TableModule,
    InplaceModule,
    Message,
    ConfirmDialog,
    InputTextModule,
    Tooltip,
    DialogModule,
    FloatLabel,
    DatePipe,
    MenubarModule,
    BreadcrumbModule,
  ],
  templateUrl: './nutzerverwaltung.html',
  styleUrl: './nutzerverwaltung.scss',
  providers: [ConfirmationService]
})
export class Nutzerverwaltung {
  private confirmService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private userService = inject(User);
  private fb = inject(FormBuilder)
  protected home: MenuItem = { label: 'Nutzerverwaltung' };
  protected menuItems: MenuItem[] = [
    {
      command: () => {
        this.newUserFormVisible = true;
      },
      label: "Nutzer erstellen"
    }
  ]

  public newUserForm = this.fb.group({
    email: ["", [Validators.required, Validators.email]],
    name: ["", [Validators.required]]
  });
  protected newUserFormSubmitted = false;
  protected newUserFormVisible = false;

  public users!: AdminUser[]

  constructor(public aRoute: ActivatedRoute) {
    this.aRoute.data.subscribe(d => {
      this.users = d['users'];
    });
  }

  public async createUser() {
    this.newUserFormSubmitted = true;
    if (this.newUserForm.valid) {
      const user = await this.userService.createUser(this.newUserForm.get("email")?.value!, this.newUserForm.get("name")?.value!)
      this.users.push(user);
      this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${user.name} wurde angelegt.`, life: 3000 });
      this.newUserForm.reset();
      this.newUserFormSubmitted = false;
    }
  }

  public isInvalid(controlName: string) {
    const control = this.newUserForm.get(controlName);
    return control?.invalid && (control.touched || this.newUserFormSubmitted);
  }

  public async confirmDelete(event: Event, user: AdminUser) {
    this.confirmService.confirm({
      position: 'top',
      target: event.currentTarget as EventTarget,
      message: `Sind Sie sicher, dass Sie <b>${user.name}</b> löschen möchten?`,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Abbrechen',
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: 'Löschen',
        severity: 'danger',
      },
      accept: async () => {
        const deletedUser = await this.userService.deleteUser(user._id);
        this.users = this.users.filter(u => u._id != deletedUser._id);
        this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${user.name} wurde gelöscht.`, life: 3000 });
      }
    });
  }

  public async toggleUserActive(user: AdminUser) {
    const newUser = await this.userService.updateUser(user._id, user.email, user.name, !user.active);
    if (newUser.active) {
      this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${newUser.name} wurde aktiviert.`, life: 3000 });
    } else {
      this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${newUser.name} wurde deaktiviert.`, life: 3000 });
    }
    this.users = this.users.map(u => {
      if (u._id == newUser._id) return newUser;
      return u;
    });
  }

  public async updateUser(updateEvent: TableEditCompleteEvent) {
    if (updateEvent.index == undefined) return;
    const user = this.users[updateEvent.index];
    const field = updateEvent.field as ("email" | "name");
    let newUser: AdminUser;
    if (field == "email") {
      newUser = await this.userService.updateUser(user._id, updateEvent.data.trim(), user.name, user.active);
      this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Email wurde zu ${newUser.email} geändert.`, life: 3000 });
    } else if (field == "name") {
      newUser = await this.userService.updateUser(user._id, user.email, updateEvent.data.trim(), user.active);
      this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Name wurde zu ${newUser.name} geändert.`, life: 3000 });
    }
    this.users = this.users.map(u => {
      if (u._id == newUser._id) return newUser;
      return u;
    });
  }
}
